// * relative
import _ from "lodash";
import React, { useRef, useState, useEffect } from "react";

// * internal
import { scrollIntoView } from "Utils";
import { AI_GEN_IDENTIFIER } from "Worksheet/modules/WorksheetConstants";
import { WORKSHEET_QUESTION_CREATION } from "Constants/stringConstants";

const styles = { height: "20vh" };

/**
 *
 * @param { containerRef, activeQuestionId, isGeneratingAIQuestions }
 * @returns Scroll manager for single page UI
 */
const SinglePageScrollController = props => {
  const {
    containerRef,
    activeQuestionId,
    isGeneratingAIQuestions,
    spaceContainerInBetweenRef,
  } = props;
  const spaceContainerRef = useRef(null);
  const [autoScrolling, setAutoScrolling] = useState(true);

  /**
   *
   * @description
   * When auto scrolling is enabled for generating AI questions,
   * disable auto-scrolling when user manually trigger scroll to up side
   *
   * when auto scrolling is disabled,
   * enable auto-scrolling when user scrolls to bottom
   */
  useEffect(() => {
    const container = containerRef?.current;

    if (!container || !isGeneratingAIQuestions) return;

    const handleScroll = () => {
      const scrollTop = container?.scrollTop;

      if (!autoScrolling) {
        // * [autoScroll = 'disabled']
        // Check if user has scrolled to bottom then enabled auto-scrolling
        const scrollHeight = container.scrollHeight;
        const clientHeight = container.clientHeight;

        // * Using threshold for better detection
        const isAtBottom =
          Math.abs(scrollHeight - clientHeight - scrollTop) < 10;

        // * If user scrolls to bottom, re-enable auto-scrolling
        if (isAtBottom) setAutoScrolling(true);
      } else {
        // * [autoScroll = 'enabled']
        // Check if user trigger scroll up, disable auto-scrolling and save the scroll position
        const isScrollUp = container.scrollTop < container._lastScrollTop;
        container._lastScrollTop = scrollTop;
        if (isScrollUp) setAutoScrolling(false);
      }
    };

    // * Initialize last scroll position
    container._lastScrollTop = container.scrollTop;
    container.addEventListener("scroll", handleScroll);

    return () => {
      if (container) container.removeEventListener("scroll", handleScroll);
    };
  }, [containerRef, isGeneratingAIQuestions, autoScrolling]);

  /**
   *
   *
   *
   * @description
   * When generating AI questions, scroll to the bottom of the container
   * to show the latest question being generated
   */
  useEffect(() => {
    let interval = null;
    const element =
      isGeneratingAIQuestions && spaceContainerInBetweenRef?.current
        ? spaceContainerInBetweenRef?.current
        : spaceContainerRef?.current;

    if (
      element &&
      autoScrolling &&
      isGeneratingAIQuestions &&
      _.includes(activeQuestionId, AI_GEN_IDENTIFIER)
    ) {
      // * Scroll to the bottom of the container when AI is generating questions and auto-scrolling is enabled
      interval = setInterval(() => {
        scrollIntoView({ element, block: "end", behavior: "smooth" });
      }, 300);
    } else {
      clearInterval(interval);
    }

    if (activeQuestionId === WORKSHEET_QUESTION_CREATION) {
      // Scroll to the top when we're on the question creation view
      scrollIntoView({
        element: containerRef?.current,
        block: "start",
        behavior: "smooth",
      });
    }

    return () => clearInterval(interval);
  }, [
    containerRef,
    isGeneratingAIQuestions,
    activeQuestionId,
    autoScrolling,
    spaceContainerInBetweenRef,
  ]);

  return <div ref={spaceContainerRef} style={styles} />;
};

export default SinglePageScrollController;
